import React from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Modal from "@/components/ui/modal";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CreditCard,
  Banknote,
  Building,
  MoreHorizontal,
  PlusCircle,
  CheckCircle2,
  Loader2,
  History,
  Receipt,
  Clock,
  FileText,
  Printer,
  Mail,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const PaymentModal = ({
  show,
  onHide,
  currencySymbol,
  paymentData,
  setPaymentData,
  processPayment,
  paymentProcessing,
  totalAmount = 0,
  paidAmount = 0,
  payments = [],
  hasPermission,
  availablePaymentMethods = [],
  onPayLater,
  payLaterLoading = false,
  onCompleteSaleWithoutPayment,
  completeSaleLoading = false,
  onPrintInvoice,
  onPrintReceipt,
  invoiceLoading = false,
  receiptGenerating = false,
  currentSaleData = null,
  customer = null,
  onSendEmail,
  emailSending = false,
  onNewSale = () => { },
}) => {
  const customerEmail = currentSaleData?.customer?.email || customer?.email;
  const hasCustomerEmail = Boolean(
    customerEmail &&
    customerEmail !== "" &&
    customerEmail !== "-" &&
    customerEmail.includes("@"),
  );
  const remainingBalance = Math.max(0, parseFloat((totalAmount - paidAmount).toFixed(2)));
  const isSaleCreated = Boolean(currentSaleData?.id) || payments.length > 0 || paidAmount > 0.001;
  const isFullyPaid =
    (totalAmount <= 0.001 && isSaleCreated) ||
    (totalAmount > 0 ? (totalAmount - paidAmount) <= 0.001 : false);

  const isCash = paymentData.payment_method && (
    (typeof paymentData.payment_method === 'string' && paymentData.payment_method.toLowerCase().includes('cash')) ||
    (availablePaymentMethods.find(m => (m.id || m.slug || m) === paymentData.payment_method)?.code === 'cash') ||
    (availablePaymentMethods.find(m => (m.id || m.slug || m) === paymentData.payment_method)?.name?.toLowerCase().includes('cash'))
  );

  const cashChange =
    !isFullyPaid && isCash && paymentData.amount > remainingBalance + 0.001
      ? parseFloat((paymentData.amount - remainingBalance).toFixed(2))
      : 0;

  const paymentMethods = availablePaymentMethods.map((pm) => {
    let icon = MoreHorizontal;
    const nameLower = (pm.name || pm).toLowerCase();
    if (nameLower.includes('cash')) icon = Banknote;
    else if (nameLower.includes('card')) icon = CreditCard;
    else if (nameLower.includes('bank')) icon = Building;
    return { id: pm.id || pm.slug || pm, label: pm.name || pm, icon };
  });

  const quickAmounts = [
    { label: "Exact", amount: null },
    { label: "+5", amount: 5 },
    { label: "+10", amount: 10 },
    { label: "+20", amount: 20 },
    { label: "+50", amount: 50 },
    { label: "Round .1", roundTo: 0.1 },
    { label: "Round 1", roundTo: 1 },
  ];

  const handleQuickAmount = (q) => {
    if (isFullyPaid) return;
    if (q.label === "Exact") {
      setPaymentData({
        ...paymentData,
        amount: parseFloat(remainingBalance.toFixed(2)),
      });
    } else if (q.roundTo !== undefined) {
      const factor = 1 / q.roundTo;
      const rounded = Math.round(remainingBalance * factor) / factor;
      setPaymentData({
        ...paymentData,
        amount: parseFloat(rounded.toFixed(2)),
      });
    } else {
      setPaymentData({
        ...paymentData,
        amount: parseFloat(((paymentData.amount || 0) + q.amount).toFixed(2)),
      });
    }
  };

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title="Checkout & Payment"
      icon={CreditCard}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex flex-col gap-3 w-full">
          {/* Top row: Sale Completion / Documents & Output Utilities */}
          {((!isSaleCreated && onCompleteSaleWithoutPayment) ||
            (isSaleCreated && (onPrintInvoice || onPrintReceipt || (hasCustomerEmail && onSendEmail)))) && (
              <div className="flex flex-wrap items-center justify-between gap-2 w-full pb-2.5 border-b border-border/60">
                <div className="flex items-center gap-2 flex-wrap">
                  {!isSaleCreated ? (
                    <>
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mr-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        Sale:
                      </span>

                      {onCompleteSaleWithoutPayment && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onCompleteSaleWithoutPayment}
                          disabled={paymentProcessing || completeSaleLoading}
                          className="h-8 gap-1.5 text-xs font-medium border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer"
                          title="Complete sale to unlock invoice and receipt options without recording immediate payment"
                        >
                          {completeSaleLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          )}
                          Complete Sale Without Payment
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mr-1">
                        <FileText className="h-3.5 w-3.5" />
                        Documents:
                      </span>

                      {onPrintInvoice && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onPrintInvoice}
                          disabled={paymentProcessing || invoiceLoading}
                          className="h-8 gap-1.5 text-xs font-medium cursor-pointer"
                          title="Print or download invoice"
                        >
                          {invoiceLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FileText className="h-3.5 w-3.5" />
                          )}
                          Invoice
                        </Button>
                      )}

                      {onPrintReceipt && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onPrintReceipt}
                          disabled={paymentProcessing || receiptGenerating}
                          className="h-8 gap-1.5 text-xs font-medium cursor-pointer"
                          title="Print thermal receipt"
                        >
                          {receiptGenerating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Printer className="h-3.5 w-3.5" />
                          )}
                          Receipt
                        </Button>
                      )}

                      {hasCustomerEmail && onSendEmail && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onSendEmail(currentSaleData)}
                          disabled={paymentProcessing || invoiceLoading || emailSending}
                          className="h-8 gap-1.5 text-xs font-medium cursor-pointer"
                          title="Send invoice email to customer"
                        >
                          {emailSending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                          Email Invoice
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {isSaleCreated && currentSaleData?.invoice_number && (
                  <div className="flex items-center">
                    <Badge variant="outline" className="text-[11px] font-semibold text-muted-foreground border-border/80">
                      Sale #{currentSaleData.invoice_number}
                    </Badge>
                  </div>
                )}
              </div>
            )}

          {/* Bottom row: Workflow & Payment Actions */}
          <div className="flex items-center justify-between gap-2.5 w-full">
            {/* Left action buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={onHide}
                disabled={paymentProcessing || payLaterLoading || completeSaleLoading}
                className="gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground"
                title="Close modal and return to POS screen"
              >
                <X className="h-4 w-4" />
                Close
              </Button>

              {!isSaleCreated && !isFullyPaid && onPayLater && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={onPayLater}
                  disabled={paymentProcessing || payLaterLoading || completeSaleLoading}
                  className="gap-1.5 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer"
                  title="Save sale and prepare for new sale without immediate payment"
                >
                  {payLaterLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Clock className="h-4 w-4" />
                  )}
                  Pay Later
                </Button>
              )}

              {isSaleCreated && !isFullyPaid && onNewSale && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={onNewSale}
                  className="gap-1.5 border-border hover:bg-muted font-medium cursor-pointer"
                  title="Finish current transaction and start a new sale"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  New Sale
                </Button>
              )}
            </div>

            {/* Right action buttons */}
            <div className="flex items-center gap-2">
              {isFullyPaid ? (
                <Button
                  size="default"
                  onClick={onNewSale || onHide}
                  className="min-w-[160px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm cursor-pointer gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Done &amp; New Sale
                </Button>
              ) : (
                <Button
                  size="default"
                  className="min-w-[150px] font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm cursor-pointer"
                  onClick={processPayment}
                  disabled={
                    paymentProcessing ||
                    completeSaleLoading ||
                    paymentData.amount <= 0 ||
                    isNaN(paymentData.amount)
                  }
                >
                  {paymentProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <PlusCircle className="mr-2 h-4 w-4" />
                      Record Payment
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Summary row */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-muted/50 border shadow-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase font-semibold text-muted-foreground mb-1">
                Total Amount
              </p>
              <p className="text-2xl font-bold tracking-tight">
                {currencySymbol}
                {totalAmount.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card
            className={cn(
              "border shadow-none",
              isFullyPaid
                ? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800"
                : "bg-slate-900 dark:bg-slate-800 border-slate-700",
            )}
          >
            <CardContent className="p-4 text-center">
              <p
                className={cn(
                  "text-xs uppercase font-semibold mb-1",
                  isFullyPaid
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-slate-400",
                )}
              >
                {isFullyPaid ? "Fully Paid" : "Balance Due"}
              </p>
              <p
                className={cn(
                  "text-2xl font-bold tracking-tight",
                  isFullyPaid
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-white",
                )}
              >
                {currencySymbol}
                {Math.max(0, remainingBalance).toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Left: input area or Fully Paid confirmation */}
          <div className="md:col-span-3 space-y-5">
            {isFullyPaid ? (
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-5 text-center space-y-3">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-emerald-900 dark:text-emerald-200">
                    Payment Complete!
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                    This sale is fully paid. You can print an invoice or thermal receipt, or click &quot;Done &amp; New Sale&quot; to prepare for the next transaction.
                  </p>
                  {hasCustomerEmail && onSendEmail && (
                    <div className="pt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onSendEmail(currentSaleData)}
                        disabled={emailSending}
                        className="gap-1.5 font-semibold"
                      >
                        {emailSending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        Email Invoice to {customerEmail}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Payment method selector */}
                <div className="space-y-3">
                  <Label className="text-xs uppercase font-semibold text-muted-foreground">
                    Payment Method
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {paymentMethods.map((method) => {
                      const Icon = method.icon;
                      const isSelected = paymentData.payment_method === method.id;
                      return (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() =>
                            setPaymentData({
                              ...paymentData,
                              payment_method: method.id,
                            })
                          }
                          className={cn(
                            "relative flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 py-3 px-2 text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground shadow-md"
                              : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground hover:bg-muted/50",
                          )}
                        >
                          {isSelected && (
                            <CheckCircle2 className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-primary-foreground" />
                          )}
                          <Icon className="h-5 w-5" />
                          <span className="text-[10px] font-semibold uppercase leading-tight">
                            {method.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Amount input */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold text-muted-foreground">
                    {isCash ? "Cash Tendered" : "Amount"} ({currencySymbol})
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-muted-foreground pointer-events-none">
                      {currencySymbol}
                    </span>
                    <Input
                      type="number"
                      className="pl-14 h-12 text-xl font-bold"
                      value={paymentData.amount}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          amount: parseFloat(e.target.value) || 0,
                        })
                      }
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                    {quickAmounts.map((q, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="font-semibold text-xs h-8 px-2 cursor-pointer"
                        onClick={() => handleQuickAmount(q)}
                      >
                        {q.label}
                      </Button>
                    ))}
                  </div>

                  {/* Change display - cash only */}
                  {cashChange > 0 && (
                    <div className="flex items-center justify-between rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 px-4 py-3">
                      <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        Change Due
                      </span>
                      <span className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                        {currencySymbol}
                        {cashChange.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Reference & notes */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold text-muted-foreground">
                    Reference / Notes
                  </Label>
                  <Input
                    placeholder="Transaction ID or reference"
                    value={paymentData.transaction_id || ""}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        transaction_id: e.target.value,
                      })
                    }
                  />
                  <Textarea
                    placeholder="Optional notes…"
                    className="min-h-[72px] resize-none"
                    value={paymentData.notes || ""}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, notes: e.target.value })
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* Right: payment history */}
          {hasPermission("pos payment read") && (
            <div className="md:col-span-2 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <History className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs uppercase font-semibold text-muted-foreground">
                  Payment History
                </span>
              </div>

              <ScrollArea className="flex-1 min-h-[180px] max-h-[240px] rounded-md border bg-muted/30 p-2">
                {payments.length === 0 ? (
                  <div className="h-40 flex flex-col items-center justify-center text-muted-foreground gap-2">
                    <Receipt className="h-8 w-8 opacity-20" />
                    <p className="text-xs font-medium">No payments yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {payments.map((p, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-md bg-background px-3 py-2 shadow-sm border"
                      >
                        <div className="min-w-0 space-y-0.5">
                          <Badge
                            variant="secondary"
                            className="capitalize text-[10px] font-semibold"
                          >
                            {typeof p.payment_method === 'object' && p.payment_method !== null ? p.payment_method.name : (availablePaymentMethods.find(m => (m.id || m.slug || m) === p.payment_method)?.name || p.payment_method || "").replace("_", " ")}
                          </Badge>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[110px]">
                            {p.transaction_id || "No reference"}
                          </p>
                        </div>
                        <span className="ml-2 font-bold text-emerald-600 whitespace-nowrap text-sm">
                          {currencySymbol}
                          {parseFloat(p.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <div className="space-y-2 pt-3 border-t">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Total Paid</span>
                  <span className="font-semibold">
                    {currencySymbol}
                    {paidAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm">Balance Due</span>
                  <span
                    className={cn(
                      "text-lg font-bold",
                      isFullyPaid ? "text-emerald-600" : "text-destructive",
                    )}
                  >
                    {currencySymbol}
                    {Math.max(0, remainingBalance).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

PaymentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  paymentData: PropTypes.object.isRequired,
  setPaymentData: PropTypes.func.isRequired,
  processPayment: PropTypes.func.isRequired,
  paymentProcessing: PropTypes.bool,
  totalAmount: PropTypes.number,
  paidAmount: PropTypes.number,
  payments: PropTypes.array,
  hasPermission: PropTypes.func.isRequired,
  availablePaymentMethods: PropTypes.array,
  onPayLater: PropTypes.func,
  payLaterLoading: PropTypes.bool,
  onCompleteSaleWithoutPayment: PropTypes.func,
  completeSaleLoading: PropTypes.bool,
  onPrintInvoice: PropTypes.func,
  onPrintReceipt: PropTypes.func,
  invoiceLoading: PropTypes.bool,
  receiptGenerating: PropTypes.bool,
  currentSaleData: PropTypes.object,
  customer: PropTypes.object,
  onSendEmail: PropTypes.func,
  emailSending: PropTypes.bool,
  onNewSale: PropTypes.func,
};

export default PaymentModal;
